'use client';

import { Popover as BasePopover } from '@base-ui-components/react/popover';

import { cn } from '../../utils/cn';
import { useInheritedColorScheme } from '../../utils/use-inherited-color-scheme';
import './popover.css';

interface PopoverProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

function Popover({ children, ...props }: PopoverProps) {
  return <BasePopover.Root {...props}>{children}</BasePopover.Root>;
}

interface PopoverTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

function PopoverTrigger({ className, ...props }: PopoverTriggerProps) {
  return (
    <BasePopover.Trigger
      className={cn(className)}
      {...(props as React.ComponentProps<typeof BasePopover.Trigger>)}
    />
  );
}

interface PopoverContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  align?: 'start' | 'center' | 'end';
}

function PopoverContent({
  className,
  side = 'bottom',
  sideOffset = 4,
  align = 'center',
  children,
  ...props
}: PopoverContentProps) {
  const { probeRef, themeAttrs } = useInheritedColorScheme();

  return (
    <>
      <span ref={probeRef} style={{ display: 'none' }} />
      <BasePopover.Portal>
        <BasePopover.Positioner
          side={side}
          sideOffset={sideOffset}
          align={align}
        >
          <BasePopover.Popup
            {...themeAttrs}
            className={cn(
              'popover-content bg-popover text-popover-foreground p-card z-50 overflow-hidden rounded-md border shadow-md',
              'transition-all duration-150',
              'data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
              'data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
              className
            )}
            {...(props as React.ComponentProps<typeof BasePopover.Popup>)}
          >
            {children}
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </>
  );
}

interface PopoverCloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

function PopoverClose({ className, ...props }: PopoverCloseProps) {
  return (
    <BasePopover.Close
      className={cn(className)}
      {...(props as React.ComponentProps<typeof BasePopover.Close>)}
    />
  );
}

export { Popover, PopoverTrigger, PopoverContent, PopoverClose };
export type {
  PopoverProps,
  PopoverTriggerProps,
  PopoverContentProps,
  PopoverCloseProps,
};
